import { useState, useCallback } from 'react';
import { useToast, getErrorMessage, useServices } from '@/app/providers';
import { compressImageForUpload } from '@yawi/infrastructure';
import type { FileCategory, UploadedFile } from '@yawi/types';

interface UseFileUploadOptions {
  onSuccess?: (file: UploadedFile) => void;
  onError?: (error: unknown) => void;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

interface UseFileUploadReturn {
  upload: (file: File, customName?: string) => Promise<UploadedFile | null>;
  isUploading: boolean;
  uploadedFile: UploadedFile | null;
  error: string | null;
  reset: () => void;
}

const DEFAULT_MAX_SIZE_MB = 25;

/**
 * Hook para subir archivos a la API
 *
 * @param category Categoría del archivo (documents, pets, avatars, etc.)
 * @param options Opciones adicionales
 *
 * @example
 * const { upload, isUploading, uploadedFile } = useFileUpload('avatars');
 *
 * const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) {
 *     const result = await upload(file);
 *     if (result) {
 *       setFormData({ ...formData, avatarUrl: result.url });
 *     }
 *   }
 * };
 */
export function useFileUpload(
  category: FileCategory,
  options: UseFileUploadOptions = {}
): UseFileUploadReturn {
  const toast = useToast();
  const { fileRepo } = useServices();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    onSuccess,
    onError,
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    allowedTypes,
  } = options;

  const upload = useCallback(
    async (file: File, customName?: string): Promise<UploadedFile | null> => {
      // Validar tamaño
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        const errorMsg = `El archivo excede el tamaño máximo de ${maxSizeMB}MB`;
        setError(errorMsg);
        toast.error(errorMsg, 'Archivo muy grande');
        onError?.(new Error(errorMsg));
        return null;
      }

      // Validar tipo si se especificó
      if (allowedTypes && allowedTypes.length > 0) {
        const isAllowed = allowedTypes.some(type => {
          if (type.endsWith('/*')) {
            const baseType = type.replace('/*', '');
            return file.type.startsWith(baseType);
          }
          return file.type === type;
        });

        if (!isAllowed) {
          const errorMsg = `Tipo de archivo no permitido. Permitidos: ${allowedTypes.join(', ')}`;
          setError(errorMsg);
          toast.error(errorMsg, 'Tipo no válido');
          onError?.(new Error(errorMsg));
          return null;
        }
      }

      const fileToUpload = await compressImageForUpload(file, category);

      setIsUploading(true);
      setError(null);

      try {
        const result = await fileRepo.uploadFile(fileToUpload, category, customName);
        setUploadedFile(result);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al subir archivo');
        onError?.(err);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [category, fileRepo, maxSizeMB, allowedTypes, onSuccess, onError, toast]
  );

  const reset = useCallback(() => {
    setIsUploading(false);
    setUploadedFile(null);
    setError(null);
  }, []);

  return { upload, isUploading, uploadedFile, error, reset };
}
